import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { toast } from 'sonner';

// India GSTIN pattern (defence-in-depth alongside the backend check).
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}[Z]{1}[A-Z0-9]{1}$/;

/**
 * GST business-details capture. Shown as a step before Razorpay checkout so
 * the invoice we later generate carries the correct company/GSTIN + place of
 * supply. All fields are optional except when the user opts in ("Claim GST
 * credit"), in which case GSTIN is required.
 */
const GstDetailsDialog = ({ open, plan, onConfirm, onCancel, couponCode = null, method = null }) => {
  const [claim, setClaim] = useState(false);
  const [states, setStates] = useState([]);
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({
    gstin: '', company_name: '', address: '',
    city: '', state: '', state_code: '', pin_code: '', mobile: '',
  });

  useEffect(() => {
    if (open) {
      api.get('/gst/states').then(r => setStates(r.data || [])).catch(() => setStates([]));
    }
  }, [open]);

  // Order summary — exact same promo + GST math as create-order, fetched
  // from /payments/quote so the buyer sees the FINAL payable before paying.
  useEffect(() => {
    if (open && plan?.id) {
      setQuote(null);
      api.post('/payments/quote', { plan_id: plan.id, coupon_code: couponCode || null })
        .then(r => setQuote(r.data))
        .catch(() => setQuote(null));
    }
  }, [open, plan?.id, couponCode]);

  // Auto-derive state from GSTIN prefix so the buyer doesn't have to pick
  // the state twice. Also fires the backend validator once the GSTIN is
  // fully typed for a friendly inline hint.
  useEffect(() => {
    const g = (form.gstin || '').trim().toUpperCase();
    if (g.length === 15 && GSTIN_RE.test(g)) {
      api.post('/gst/validate', { gstin: g })
        .then(r => {
          if (r.data?.ok && r.data.state_code) {
            setForm(f => ({ ...f, state_code: r.data.state_code, state: r.data.state || f.state }));
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.gstin]);

  const stateOptions = states.length > 0 ? states : [
    { code: '07', name: 'Delhi' }, { code: '27', name: 'Maharashtra' },
    { code: '29', name: 'Karnataka' }, { code: '33', name: 'Tamil Nadu' },
  ];

  const proceed = (withDetails) => {
    if (withDetails && claim) {
      const g = (form.gstin || '').trim().toUpperCase();
      if (!GSTIN_RE.test(g)) {
        toast.error('Enter a valid 15-character GSTIN');
        return;
      }
    }
    const payload = withDetails ? {
      gstin: claim ? (form.gstin || '').trim().toUpperCase() : null,
      company_name: form.company_name || null,
      address: form.address || null,
      city: form.city || null,
      state: form.state || null,
      state_code: form.state_code || null,
      pin_code: form.pin_code || null,
      mobile: form.mobile || null,
    } : null;
    onConfirm(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel?.(); }}>
      <DialogContent className='max-w-lg' data-testid='gst-details-dialog'>
        <DialogHeader>
          <DialogTitle>GST / Business details (optional)</DialogTitle>
          <DialogDescription>
            Add your business details to receive a GST-compliant tax invoice for <strong>{plan?.name}</strong>. Skip this to purchase as an individual — you'll still receive a receipt.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          {quote && (
            <div className='rounded-lg border bg-secondary/40 p-3 text-sm space-y-1' data-testid='order-summary-block'>
              <div className='text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1'>Order summary</div>
              <div className='flex justify-between'><span>Base price</span><span className='tabular-nums'>₹ {Number(quote.base_price).toFixed(2)}</span></div>
              {quote.discount > 0 && (
                <div className='flex justify-between text-[hsl(var(--verified))]' data-testid='order-summary-discount'>
                  <span>Discount{quote.promotion_name ? ` (${quote.promotion_name})` : ''}</span>
                  <span className='tabular-nums'>− ₹ {Number(quote.discount).toFixed(2)}</span>
                </div>
              )}
              <div className='flex justify-between'><span>Taxable amount</span><span className='tabular-nums'>₹ {Number(quote.taxable_amount).toFixed(2)}</span></div>
              <div className='flex justify-between'><span>GST ({quote.gst_rate_pct}%)</span><span className='tabular-nums'>₹ {Number(quote.total_tax).toFixed(2)}</span></div>
              <div className='flex justify-between font-semibold border-t pt-1' data-testid='order-summary-total'>
                <span>Total payable</span><span className='tabular-nums'>₹ {Number(quote.grand_total).toFixed(2)}</span>
              </div>
              {method === 'points' && (
                <div
                  className={`mt-1 rounded border px-2 py-1.5 text-xs ${quote.points_sufficient ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-red-300 bg-red-50 text-red-700'}`}
                  data-testid='order-summary-points'
                >
                  Rewards Points required: <strong>{Number(quote.points_required).toLocaleString('en-IN')}</strong>
                  {' '}({quote.points_per_inr} pts = ₹1) · Your balance: <strong>{Number(quote.points_balance).toLocaleString('en-IN')}</strong>
                  {!quote.points_sufficient && <div className='mt-0.5 font-medium'>Insufficient points for this purchase.</div>}
                </div>
              )}
            </div>
          )}
          <label className='flex items-center gap-2 text-sm cursor-pointer'>
            <input
              type='checkbox'
              checked={claim}
              onChange={(e) => setClaim(e.target.checked)}
              data-testid='gst-claim-toggle'
            />
            I want to claim GST input credit (requires GSTIN)
          </label>

          {claim && (
            <>
              <div>
                <Label>GSTIN *</Label>
                <Input
                  value={form.gstin}
                  onChange={(e) => setForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))}
                  placeholder='27AAAAA0000A1Z5'
                  maxLength={15}
                  className='font-mono'
                  data-testid='gst-gstin-input'
                />
                {form.gstin && form.gstin.length === 15 && !GSTIN_RE.test(form.gstin) && (
                  <div className='text-xs text-red-600 mt-1'>Invalid GSTIN format</div>
                )}
              </div>
              <div>
                <Label>Company / Business Name</Label>
                <Input value={form.company_name} onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))} data-testid='gst-company-input' />
              </div>
            </>
          )}

          <div>
            <Label>Billing address</Label>
            <Textarea rows={2} value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} data-testid='gst-address-input' />
          </div>

          <div className='grid grid-cols-3 gap-3'>
            <div className='col-span-2'>
              <Label>State (Place of Supply)</Label>
              <Select value={form.state_code} onValueChange={(v) => {
                const s = stateOptions.find(x => x.code === v);
                setForm(f => ({ ...f, state_code: v, state: s?.name || '' }));
              }}>
                <SelectTrigger data-testid='gst-state-select'><SelectValue placeholder='Choose state' /></SelectTrigger>
                <SelectContent className='max-h-72'>
                  {stateOptions.map(s => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>PIN</Label>
              <Input value={form.pin_code} maxLength={6} onChange={(e) => setForm(f => ({ ...f, pin_code: e.target.value.replace(/\D/g, '') }))} data-testid='gst-pin-input' />
            </div>
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.city} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} data-testid='gst-city-input' />
          </div>
        </div>

        <DialogFooter className='flex-col sm:flex-row gap-2'>
          <Button variant='ghost' onClick={() => proceed(false)} disabled={method === 'points' && quote && !quote.points_sufficient} data-testid='gst-skip-btn'>
            Skip &amp; continue
          </Button>
          <Button onClick={() => proceed(true)} disabled={method === 'points' && quote && !quote.points_sufficient} data-testid='gst-continue-btn'>
            Save &amp; continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GstDetailsDialog;
